import {call, put, select, take} from "typed-redux-saga";
import {get} from "lodash";
import {
	changeRequestState,
	editUserAnswers,
	getIsFriendlyCountry,
	getIsFriendlyCountryFailed,
	getIsFriendlyCountrySuccess,
	requestContest,
	requestContestSuccess,
	requestUserAnswers,
	requestUserAnswersFailed,
	requestUserAnswersSuccess,
	saveUserAnswers,
	saveUserAnswersFailed,
	saveUserAnswersSuccess,
	setAnswers,
	showErrorModal,
	showModal,
} from "modules/actions";
import {
	Api,
	ApiError,
	createConnextraScriptTag,
	generateUniqId,
	getLang,
	Storage,
	trackIdentify,
} from "modules/utils";
import {getAnswers, getContest, getProfile, getUser} from "modules/selectors";
import {RequestState} from "modules/enums";
import {ANSWERS_STORAGE_KEY} from "modules/constants";
import {IAnswer} from "modules/reducers";

export const requestLandingDataSaga = function* () {
	try {
		const profile = yield* select(getProfile);
		const hasTerms = Boolean(profile?.terms);

		yield* put(requestContest());
		yield* put(getIsFriendlyCountry());

		if (hasTerms) {
			yield* take(requestContestSuccess);
			yield* put(requestUserAnswers());
		} else {
			yield* call(getUnLoggedAnswersSaga);
		}
	} catch (err) {
		const {message} = err as ApiError;
		yield* put(showErrorModal({message}));
	}
};
// TODO the logic does not work if the questions in the CMS have been updated
export const requestUserAnswersSaga = function* () {
	try {
		const result = yield* call(Api.Predictor.get);
		const savedOptions = result.success.options;

		const contest = yield* select(getContest);

		const answers: IAnswer[] = get(contest, "questions", []).map(({id, options}) => ({
			questionId: id,
			optionId: options.find(({id}) => savedOptions.includes(id))!.id,
		}));

		yield* put(requestUserAnswersSuccess({answers, savedOptions}));
		Storage.REMOVE(ANSWERS_STORAGE_KEY);
	} catch (err) {
		console.log(err);
		yield* put(requestUserAnswersFailed());
		yield* call(saveUnSavedAnswersSaga);
	}
};

export const saveUserAnswersSaga = function* ({payload}: ReturnType<typeof saveUserAnswers>) {
	try {
		const answers = yield* select(getAnswers);
		const user = yield* select(getUser);

		if (!answers || !user) {
			return;
		}

		const result = yield* call(Api.Predictor.save, {
			answers: answers.map(({optionId}) => optionId),
			lang: getLang(),
			email: user.email,
			notification: true,
		});

		yield* put(saveUserAnswersSuccess({savedOptions: result.success.options}));
		yield* put(showModal("savedAnswersModal"));
		trackIdentify(user.email);
		createConnextraScriptTag(
			`https://zz.connextra.com/dcs/tagController/tag/8ce716f59752/f2p_preseasonfutures_submission?AccountID=${generateUniqId()}`
		);
		Storage.REMOVE(ANSWERS_STORAGE_KEY);
	} catch (err) {
		if (err instanceof ApiError) {
			if (err.code === 403) {
				yield* put(saveUserAnswersFailed({"": err.message}));
			} else {
				yield* put(showErrorModal(err));
				yield* put(changeRequestState(RequestState.Received));
			}
		}
	}
};

export const editUserAnswersSaga = function* ({payload}: ReturnType<typeof editUserAnswers>) {
	try {
		const answers = yield* select(getAnswers);
		const user = yield* select(getUser);

		if (!answers || !user) {
			return;
		}

		const result = yield* call(Api.Predictor.edit, {
			answers: answers.map(({optionId}) => optionId),
			lang: getLang(),
			email: user.email,
			notification: true,
		});

		yield* put(saveUserAnswersSuccess({savedOptions: result.success.options}));
		yield* put(showModal("savedAnswersModal"));
	} catch (err) {
		if (err instanceof ApiError) {
			if (err.code === 403) {
				yield* put(saveUserAnswersFailed({"": err.message}));
			} else {
				yield* put(showErrorModal(err));
				yield* put(changeRequestState(RequestState.Received));
			}
		}
	}
};

export const getIsFriendlyCountrySaga = function* () {
	try {
		const result = yield* call(Api.Predictor.isFriendlyCountry);

		yield* put(getIsFriendlyCountrySuccess(result.success.status));
	} catch (err) {
		yield* put(getIsFriendlyCountryFailed());

		if (err instanceof ApiError) {
			yield* put(showErrorModal(err));
		}
	}
};

export const saveUnLoggedAnswersSaga = function* () {
	try {
		const answers = yield* select(getAnswers);
		Storage.SET(ANSWERS_STORAGE_KEY, JSON.stringify({answers}));
	} catch (err) {
		const {message} = err as Error;
		yield* put(showErrorModal({message}));
	}
};

export const getUnLoggedAnswersSaga = function* () {
	try {
		const unSavedAnswers = Storage.GET(ANSWERS_STORAGE_KEY);

		if (!unSavedAnswers) {
			return;
		}

		const {answers} = JSON.parse(unSavedAnswers) as {answers: IAnswer[]};

		yield* put(setAnswers(answers));
	} catch (err) {
		console.error(err);
		Storage.REMOVE(ANSWERS_STORAGE_KEY);
	}
};

export const saveUnSavedAnswersSaga = function* () {
	try {
		const unSavedAnswers = Storage.GET(ANSWERS_STORAGE_KEY);

		if (!unSavedAnswers) {
			return;
		}

		const {answers} = JSON.parse(unSavedAnswers) as {answers: IAnswer[]};

		yield* put(setAnswers(answers));
		yield* put(saveUserAnswers());
	} catch (err) {
		console.error(err);
		Storage.REMOVE(ANSWERS_STORAGE_KEY);
	}
};
